import { useCallback, useState } from 'react';

export type ColumnId =
  | 'status'
  | 'setup'
  | 'clinic'
  | 'device'
  | 'remoteMonitored'
  | 'diagnosis'
  | 'following'
  | 'demographics'
  | 'insurance'
  | 'lastTransmission'
  | 'lastCompletedReport'
  | 'openReport';

export interface ColumnDef {
  id: ColumnId;
  label: string;
  width: number;
  visible: boolean;
}

export type Density = 'comfortable' | 'compact';

/**
 * Saved preferences only store `{ id, visible }` and are merged onto this list by id, so a new
 * column here shows for users with saved preferences too (with its default visibility unless
 * they changed it). Order and widths follow the POC list (widths are min widths).
 *
 * Setup, demographics, and insurance stay in Enrollment mode (internal users).
 * Diagnosis and Following start hidden; any user can turn them on from Columns.
 * Remote Monitored stays off the list until /patients/v2/list returns isRemoteMonitored.
 */
export const DEFAULT_COLUMNS: ColumnDef[] = [
  { id: 'status', label: 'Status', width: 110, visible: true },
  { id: 'setup', label: 'Setup', width: 130, visible: true },
  { id: 'clinic', label: 'Clinic', width: 150, visible: true },
  { id: 'device', label: 'Device', width: 200, visible: true },
  { id: 'diagnosis', label: 'Diagnosis', width: 220, visible: false },
  { id: 'following', label: 'Following', width: 220, visible: false },
  { id: 'demographics', label: 'Demographics', width: 150, visible: false },
  { id: 'insurance', label: 'Insurance', width: 150, visible: false },
  { id: 'lastTransmission', label: 'Last Transmission', width: 140, visible: true },
  { id: 'openReport', label: 'Most Recent Report', width: 220, visible: true },
  { id: 'lastCompletedReport', label: 'Last Completed Report', width: 200, visible: true }
];

export function useColumnPreferences() {
  const [columns, setColumns] = useState<ColumnDef[]>(DEFAULT_COLUMNS);
  const [density, setDensityState] = useState<Density>('comfortable');

  const setColumnVisibility = useCallback((id: ColumnId, visible: boolean) => {
    setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, visible } : c)));
  }, []);

  const reorderColumns = useCallback((newOrder: ColumnDef[]) => {
    setColumns(newOrder);
  }, []);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
  }, []);

  return { columns, setColumnVisibility, reorderColumns, density, setDensity };
}
